import { useEffect } from "react";
import type { ItemListId } from "../../../tracking/create-ecommerce-event";
import type { BloodTestPackage } from "../../models/blood-test-package";
import { safeTrackEvent, viewItemListEvent } from "../../tracking/events";
import { CatalogItem } from "./catalog-item";

interface CatalogListProps {
  vouchers: BloodTestPackage[];
  getRowKey?: (voucher: BloodTestPackage) => string;
  allowMultiple?: boolean;
  selectedProductUuids: string[];
  onAddProduct: (product: BloodTestPackage, itemListId?: ItemListId, index?: number) => void;
  onRemoveProduct: (product: BloodTestPackage, itemListId?: ItemListId, index?: number) => void;
  onClearCart: () => void;
  enableQuantity?: boolean;
  productQuantities?: Record<string, number>;
  simplified?: boolean;
  itemListId?: ItemListId;
}

export function CatalogList({
  vouchers,
  getRowKey,
  allowMultiple = false,
  selectedProductUuids,
  onAddProduct,
  onRemoveProduct,
  onClearCart,
  enableQuantity = false,
  productQuantities,
  simplified = false,
  itemListId = "listing",
}: CatalogListProps) {
  const rowKey = getRowKey ?? ((v: BloodTestPackage) => v.uuid);

  const onToggleProduct = (voucher: BloodTestPackage, index: number) => {
    const isSelected = selectedProductUuids.includes(rowKey(voucher));

    if (enableQuantity) {
      if (!allowMultiple && !isSelected && selectedProductUuids.length > 0) {
        onClearCart();
      }
      onAddProduct(voucher, itemListId, index);
      return;
    }

    if (allowMultiple) {
      if (isSelected) {
        onRemoveProduct(voucher, itemListId, index);
      } else {
        onAddProduct(voucher, itemListId, index);
      }
    } else {
      onClearCart();

      if (!isSelected) {
        onAddProduct(voucher, itemListId, index);
      }
    }
  };

  const voucherIds = vouchers.map((v) => v.uuid).join(",");
  // voucherIds stabilizes the array dep to prevent redundant view_item_list fires (REV-92).
  // itemListId is included so the same set of UUIDs shown in a different context (e.g.
  // listing → wyniki_wyszukiwania) re-fires with the correct attribution.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useEffect(() => {
    if (vouchers && vouchers.length > 0) {
      safeTrackEvent(
        () =>
          viewItemListEvent(
            vouchers.map((product) => ({
              ...product,
              productType: product.productType ?? "blood-test-package",
              quantity: 1,
              item_list_id: itemListId,
            })),
          ),
        "view_item_list",
        "catalog-list",
      );
    }
  }, [voucherIds, itemListId]);

  return (
    <div className="grid w-full grid-cols-1 gap-2 self-start lg:gap-3">
      {vouchers.map((voucher, idx) => (
        <CatalogItem
          key={rowKey(voucher)}
          voucher={voucher}
          isSelected={selectedProductUuids.includes(rowKey(voucher))}
          onAddToCart={() => onToggleProduct(voucher, idx + 1)}
          quantity={productQuantities?.[rowKey(voucher)]}
          simplified={simplified}
          itemListId={itemListId}
          index={idx + 1}
        />
      ))}
    </div>
  );
}
